import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { PurchaseOrderEntry, PurchaseOrderUpdateInput } from "../type/purchase-order";
import { translate } from "../../../core";
import { Button, Card, Checkbox, Flex, Form, Input, Modal, Row, Space, Tag } from "antd";
import { InfoTooltip, NumericInput, StimulsoftViewer } from "../../../core/component";
import {
  dateDisplay,
  DateFormatMonthText,
  resolveNumber, uniqueRandomString,
  usdFormat,
} from "../../../core/until";
import { PurchaseOrderSourceTag } from "./PurchaseOrderEntryComponent";
import Enumerable from "linq";
import { PurchaseOrderListItemParcel } from "./PurchaseOrderListItemParcel";
import { ContactAddress, ContactWebInfo, PhoneType } from "../../contact/type/contact";
import {
  CheckOutlined,
  DeleteOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import {
  PurchaseOrderConfirm,
  PurchaseOrderDelete,
  PurchaseOrderQuery,
  PurchaseOrderUpdate,
} from "../graphql/purchase-order.graphql";
import { PurchaseOrderListItemContext } from "./PurchaseOrderListItemContext";
import { purchaseOrderStatusMapper, purchaseOrderStatusLabelMapper } from "../func/purchaseOrderStatusMapper";
import { PurchaseOrderSpecialInstruction } from "./PurchaseOrderSpecialInstruction";

export const PurchaseOrderListItem: FC = () => {
  const {entity, message, onAfterDelete} = useContext(PurchaseOrderListItemContext);
  const [form] = Form.useForm<PurchaseOrderEntry>();
  const [reportOpen, setReportOpen] = useState<boolean>(false);
  const [printKey, setPrintKey] = useState<string>(uniqueRandomString())
  // A saved Special Instruction marks the report outdated; it reloads when
  // next opened instead of on every auto-save.
  const reportOutdatedRef = useRef(false)

  const openReport = () => {
    if (reportOutdatedRef.current) {
      reportOutdatedRef.current = false
      setPrintKey(uniqueRandomString())
    }
    setReportOpen(true)
  }

  const contactInfo = useMemo(() => {
    if (!entity.vendor) return null;
    const {
      vendor: {addresses, webInfos, phones, accountNumber},
    } = entity;
    const address = addresses[0] ?? ({} as ContactAddress);

    const phone =
      phones?.find((ph) => ph.type === PhoneType.Office)?.number ??
      phones?.find((ph) => ph.type === PhoneType.Cell)?.number;
    const {value: email} =
    webInfos?.find((wi) => wi.type == "EmailAddress") ??
    ({} as ContactWebInfo);

    return {
      line1: [address.street, address.city, address.state, address.zipCode]
        .filter(Boolean)
        .join(),
      line2: [phone ? `${translate("phone")}: ${phone}` : null].filter(Boolean),
      line3: [email ? `${translate("email")}: ${email}` : null]
        .filter(Boolean)
        .join(" | "),
      line4: accountNumber
        ? `${translate("account", "number")}: ${accountNumber}`
        : null,
      email,
    };
  }, [entity]);

  const backOrderNumbersByItemId = useMemo(() => {
    const map = new Map<number, number[]>();

    for (const backOrder of entity.backOrders ?? []) {
      const itemId = backOrder?.orderLine?.itemId;
      const orderNumber = backOrder?.orderLine?.order?.number;

      if (!itemId || !orderNumber) continue;

      const numbers = map.get(itemId) ?? [];

      if (!numbers.includes(orderNumber))
        map.set(itemId, [...numbers, orderNumber]);
    }

    return map;
  }, [entity]);

  useEffect(() => {
    form.setFieldsValue(entity);
  }, [entity]);

  const [update] = useMutation(PurchaseOrderUpdate, {
    onCompleted: () => {
      message.success(translate("purchase_order", "save_success_message"))
      setPrintKey(uniqueRandomString())
    },
    onError: (error) => message.error(error.message),
    refetchQueries: [PurchaseOrderQuery],
  });

  const [saveForConfirm, {loading: savingForConfirm}] = useMutation(
    PurchaseOrderUpdate,
    {
      onError: (error) => message.error(error.message),
      refetchQueries: [PurchaseOrderQuery],
      awaitRefetchQueries: true,
    }
  );

  const [confirm, {loading: confirming}] = useMutation(PurchaseOrderConfirm, {
    onCompleted: () => {
      message.success(translate("purchase_order", "confirm_success_message"))
      setPrintKey(uniqueRandomString())
    },
    onError: (error) => message.error(error.message),
    refetchQueries: [PurchaseOrderQuery],
  });

  const [deleteRow, {loading: deleting}] = useMutation(PurchaseOrderDelete, {
    onCompleted: () => {
      message.success(translate("purchase_order", "delete_success_message"))
      setPrintKey(uniqueRandomString())
      onAfterDelete?.()
    },
    onError: (error) => message.error(error.message),
    refetchQueries: [PurchaseOrderQuery],
  });

  const handleUpdate = (entry: PurchaseOrderEntry) => {
    const deleteItems = entity.items
      .filter((it) => !entry.items.some((en) => en.id === it.id))
      .map((it) => it.id);

    return saveForConfirm({
      variables: {
        input: {
          id: entity?.id,
          note: entry.note,
          showNote: entry.showNode,
          items: {
            updatePrice: entry.items.map((it) => ({
              id: it.id,
              price: it.price,
            })),
            delete: deleteItems,
          },
        },
      },
    });
  };

  const showNoteCheckbox = (
    <Form.Item
      name='showNote'
      valuePropName='checked'
    >
      <Checkbox
        onChange={e => update({
          variables: {
            input: {
              id: entity.id,
              showNote: e.target.checked,
            } as PurchaseOrderUpdateInput
          }
        })}
      >
        {translate('show')}
      </Checkbox>
    </Form.Item>
  );

  const handleSaveAndConfirm = (entry: PurchaseOrderEntry) => {
    Modal.confirm({
      title: translate("save_and_confirm", "purchase_order"),
      content: translate("sure_message"),
      onOk: async () => {
        try {
          const result = await handleUpdate(entry);
          if (result?.errors?.length) return;
          await confirm({ variables: { id: entity.id } });
        } catch {
          // Errors are already surfaced by the mutations' onError handlers.
        }
      },
    });
  };

  return (
    <Form form={form} onFinish={handleSaveAndConfirm}>
      <Space direction="vertical" className="w-100">
        <Card className="w-100 border">
          <Flex>
            <Flex className="w-50">
              <Space direction="vertical">
                <h4>
                  {translate("purchase_order")}
                  <span className="ms-2">
                    #{translate("purchase_order_abbr")}
                    {entity.number}
                  </span>
                  <InfoTooltip contentKey={`poStatus.${entity.status}`}>
                    <Tag
                      bordered
                      color={purchaseOrderStatusMapper[entity.status]}
                      className="fs-5 p-1 mx-2"
                      style={{ cursor: "help" }}
                    >
                      {purchaseOrderStatusLabelMapper[entity.status]}
                    </Tag>
                  </InfoTooltip>
                </h4>

                <div className="text-muted f-medium">
                  <span>{translate("created_at")} : </span>
                  <span>
                    {dateDisplay(entity.createdAt, DateFormatMonthText)}
                  </span>
                </div>
              </Space>
            </Flex>

            <Flex justify="end" className="w-50">
              <Space>
                {entity.status === "Pending" ? (
                  <Button
                    loading={savingForConfirm || confirming}
                    onClick={form.submit}
                    type="primary"
                    icon={<CheckOutlined/>}
                  >
                    {translate("save_and_confirm")}
                  </Button>
                ) : null}

                <Button
                  icon={<PrinterOutlined/>}
                  onClick={openReport}
                >
                  {translate("report", "/", "email")}
                </Button>

                {entity.status === "Pending" ||
                entity.status === "Confirmed" ? (
                  <Button
                    loading={deleting}
                    danger
                    icon={<DeleteOutlined/>}
                    onClick={() =>
                      Modal.confirm({
                        title: translate(
                          "delete",
                          "purchase_order",
                          `#${entity.number}`
                        ),
                        content: translate("sure_message"),
                        onOk: () =>
                          deleteRow({
                            variables: {
                              id: entity.id,
                            },
                          }),
                      })
                    }
                  >
                    {translate("delete")}
                  </Button>
                ) : null}
              </Space>
            </Flex>
          </Flex>
        </Card>

        <Space direction="vertical" className="w-100">
          <Card className="w-100 border">
            <Flex className="f-medium">
              <Flex className="w-50" vertical>
                <Row>
                  <h5>{translate("supplier", "information")}</h5>
                </Row>

                <Row>
                  <h5 style={{color: "#16a34a"}}>
                    {entity?.vendor?.contactId}
                  </h5>
                </Row>
                <div className="text-muted">{contactInfo?.line1}</div>
                <div className="text-muted">{contactInfo?.line2}</div>
                <div className="text-muted">{contactInfo?.line3}</div>
                {contactInfo?.line4 ? (
                  <div className="text-muted">{contactInfo.line4}</div>
                ) : null}
                <Row></Row>
              </Flex>

              <Flex className="w-50" vertical>
                {entity.status === 'Pending' ? (
                  // Pending: the note is part of the form and saved by Save & Confirm.
                  <>
                    <Row>
                      <Space>
                        <h5>{translate("special_instruction")}</h5>
                      </Space>
                    </Row>

                    <Row>{showNoteCheckbox}</Row>

                    <Row>
                      <Form.Item className="w-100" name="note">
                        <Input.TextArea/>
                      </Form.Item>
                    </Row>
                  </>
                ) : (
                  <PurchaseOrderSpecialInstruction
                    key={entity.id}
                    purchaseOrderId={entity.id}
                    value={entity.note}
                    onSaved={() => { reportOutdatedRef.current = true }}
                  >
                    {showNoteCheckbox}
                  </PurchaseOrderSpecialInstruction>
                )}
              </Flex>
            </Flex>
          </Card>

          <Card className="w-100 border f-medium">
            <Form.List name="items">
              {(items, {remove}) => (
                <Form.Item shouldUpdate>
                  {({getFieldValue}) => (
                    <table className="table" style={{tableLayout: "fixed"}}>
                      <colgroup>
                        <col style={{width: "9%"}}/>
                        <col style={{width: "19%"}}/>
                        <col style={{width: "16%"}}/>
                        <col style={{width: "8%"}}/>
                        <col style={{width: "9%"}}/>
                        <col style={{width: "10%"}}/>
                        <col style={{width: "10%"}}/>
                        <col style={{width: "14%"}}/>
                        <col style={{width: "5%"}}/>
                      </colgroup>
                      <thead>
                      <tr>
                        <th>{translate("sku")}</th>
                        <th>{translate("description")}</th>
                        <th>{translate("style_pattern")}</th>
                        <th>{translate("quantity")}</th>
                        <th>{translate("quantity", "received")}</th>
                        <th>{translate("cost")}</th>
                        <th>{translate("total")}</th>
                        <th>{translate("source")}</th>
                        <th></th>
                      </tr>
                      </thead>
                      <tbody style={{overflowWrap: "break-word"}}>
                      {items
                        .sort((a, b) => {
                          const skuA = getFieldValue(["items", a.name, "item", "sku"]);
                          const skuB = getFieldValue(["items", b.name, "item", "sku"]);
                          return +skuA - +skuB;// Sorts in ascending order
                        })
                        .map(({name}) => (
                          <tr key={name}>
                            <td>
                              <Form.Item shouldUpdate>
                                {({getFieldValue}) =>
                                  getFieldValue(["items", name, "item", "sku"])
                                }
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item shouldUpdate>
                                {({getFieldValue}) =>
                                  getFieldValue(["items", name, "description"]) ??
                                  getFieldValue(["items", name, "item", "description"])
                                }
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item shouldUpdate>
                                {({getFieldValue}) =>
                                  getFieldValue(["items", name, "item", "style"])
                                }
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item shouldUpdate>
                                {({getFieldValue}) =>
                                  getFieldValue(["items", name, "quantity"])
                                }
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item shouldUpdate>
                                {({getFieldValue}) =>
                                  getFieldValue(["items", name, "quantityReceived"])
                                }
                              </Form.Item>
                            </td>
                            <td>
                              {entity.status === "Pending" ? (
                                <Form.Item
                                  name={[name, "price"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: translate("price", "is_required"),
                                    },
                                  ]}
                                >
                                  <NumericInput/>
                                </Form.Item>
                              ) : (
                                <Form.Item shouldUpdate>
                                  {({getFieldValue}) =>
                                    usdFormat(
                                      getFieldValue(["items", name, "price"])
                                    )
                                  }
                                </Form.Item>
                              )}
                            </td>
                            <td>
                              <Form.Item shouldUpdate>
                                {({getFieldValue}) => {
                                  const item = getFieldValue(["items", name]) as PurchaseOrderEntry["items"][number];
                                  return usdFormat(
                                    resolveNumber(item.quantity) *
                                    resolveNumber(item.price)
                                  );
                                }}
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item shouldUpdate>
                                {({getFieldValue}) => (
                                  <PurchaseOrderSourceTag
                                    value={getFieldValue(["items", name, "source"])}
                                    orderNumbers={backOrderNumbersByItemId.get(
                                      getFieldValue(["items", name, "item", "id"])
                                    )}
                                  />
                                )}
                              </Form.Item>
                            </td>
                            <td>
                              {entity.status === "Pending" ? (
                                <Button
                                  danger
                                  type="text"
                                  shape="circle"
                                  icon={<DeleteOutlined/>}
                                  onClick={() =>
                                    Modal.confirm({
                                      title: translate("delete", "product"),
                                      content: translate("sure_message"),
                                      onOk: () => remove(name),
                                    })
                                  }
                                />
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                      <tr>
                        <td colSpan={6}>
                          <span className="fs-5">{translate("total")}</span>
                        </td>
                        <td>
                          <Form.Item shouldUpdate noStyle>
                            {({getFieldValue}) => (
                              <span className="ms-1 fs-5">
                                  {usdFormat(
                                    Enumerable.from(
                                      getFieldValue("items") as PurchaseOrderEntry["items"]
                                    ).sum((it) => it.price * it.quantity)
                                  )}
                                </span>
                            )}
                          </Form.Item>
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                      </tfoot>
                    </table>
                  )}
                </Form.Item>
              )}
            </Form.List>
          </Card>

          {entity.status === "Pending" ? null : <PurchaseOrderListItemParcel/>}
        </Space>
      </Space>

      <Modal
        closable
        open={reportOpen}
        footer={[]}
        width="60%"
        onCancel={() => setReportOpen(false)}
      >
        <div className="p-1">
          <StimulsoftViewer
            key={printKey}
            cartStyle={{width: "unset"}}
            name="purchase-order-print"
            request={{id: entity?.id}}
            skip={!entity?.id}
            email={{
              defaultEmailAddress: contactInfo?.email,
            }}
          />
        </div>
      </Modal>
    </Form>
  );
};
